import { roundTo } from '../../../shared/chart-shared';
import type { HistogramConfig, YAxisType } from './histogram-chart.model';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ComputedHistogram {
  readonly labels: string[];
  readonly values: number[];
  readonly binEdges: number[];
}

export function computeHistogram(data: number[], config: HistogramConfig): ComputedHistogram {
  if (data.length === 0) {
    return { labels: [], values: [], binEdges: [] };
  }

  const edges = computeBinEdges(data, config);

  if (edges.length < 2) {
    return { labels: [], values: [], binEdges: [] };
  }

  const frequencies = countFrequencies(data, edges);
  const values = transformYValues(frequencies, edges, data.length, config.yAxisType);
  const labels = buildLabels(edges);

  return { labels, values, binEdges: edges };
}

// ---------------------------------------------------------------------------
// Step 1 — Bin edges
// ---------------------------------------------------------------------------

function computeBinEdges(data: number[], config: HistogramConfig): number[] {
  const dataMin = Math.min(...data);
  const dataMax = Math.max(...data);

  if (dataMin === dataMax) {
    return [dataMin, dataMin + 1];
  }

  const start = config.startBoundary ?? dataMin;

  switch (config.binning.mode) {
    case 'count': {
      const end = Math.max(dataMax, start);
      const range = end - start;
      const width = range / config.binning.binCount;
      if (width <= 0) {
        return [start, start + 1];
      }
      return Array.from({ length: config.binning.binCount + 1 }, (_, i) => start + i * width);
    }

    case 'width': {
      const binWidth = config.binning.binWidth;
      if (binWidth <= 0) {
        return [start, start + 1];
      }
      const edges: number[] = [start];
      let edge = start;
      while (edge < dataMax) {
        edge += binWidth;
        edges.push(edge);
      }
      // Ensure we have at least 2 edges
      if (edges.length < 2) {
        edges.push(start + binWidth);
      }
      return edges;
    }

    case 'custom': {
      const sorted = [...config.binning.boundaries].sort((a, b) => a - b);
      // Need at least 2 boundaries to form 1 bin
      if (sorted.length < 2) {
        return [dataMin, dataMax];
      }
      return sorted;
    }
  }
}

// ---------------------------------------------------------------------------
// Step 2 — Count frequencies
// ---------------------------------------------------------------------------

function countFrequencies(data: number[], edges: number[]): number[] {
  const binCount = edges.length - 1;
  const frequencies = new Array<number>(binCount).fill(0);
  const firstEdge = edges[0];
  const lastEdge = edges[edges.length - 1];

  for (const value of data) {
    // Clamp values below first edge into first bin
    if (value < firstEdge) {
      frequencies[0]++;
      continue;
    }
    // Clamp values above last edge into last bin
    if (value > lastEdge) {
      frequencies[binCount - 1]++;
      continue;
    }

    for (let i = 0; i < binCount; i++) {
      const inBin =
        i === binCount - 1 ? value >= edges[i] && value <= edges[i + 1] : value >= edges[i] && value < edges[i + 1];

      if (inBin) {
        frequencies[i]++;
        break;
      }
    }
  }

  return frequencies;
}

// ---------------------------------------------------------------------------
// Step 3 — Transform y-axis values
// ---------------------------------------------------------------------------

function transformYValues(frequencies: number[], edges: number[], totalN: number, yAxisType: YAxisType): number[] {
  switch (yAxisType) {
    case 'frequency':
      return frequencies;

    case 'relative-frequency':
      return frequencies.map((f) => (totalN > 0 ? roundTo((f / totalN) * 100, 2) : 0));

    case 'frequency-density':
      return frequencies.map((f, i) => {
        const width = edges[i + 1] - edges[i];
        return width > 0 && totalN > 0 ? roundTo((f / (totalN * width)) * 100, 4) : 0;
      });
  }
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

function buildLabels(edges: number[]): string[] {
  const labels: string[] = [];
  for (let i = 0; i < edges.length - 1; i++) {
    labels.push(`${formatEdge(edges[i])}–${formatEdge(edges[i + 1])}`);
  }
  return labels;
}

function formatEdge(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(1);
}
